import { ColDef, GridApi } from 'ag-grid-community';
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import TechnicianSelectFilter from 'components/tables/filters/TechnicianSelectFilter';
import { RevisionStatusFloatingFilter } from 'components/tables/filters/revision-status/RevisionStatusFloatingFilter';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import { useApi } from 'api/api-context';
import RevisionStatusFilter from 'components/tables/filters/revision-status/RevisionStatusFilter';
import KepMessageUuidColumn from 'components/tables/data/signature/columns/KepMessageUuidColumn';
import StatusColumn from 'components/tables/data/signature/columns/StatusColumn';
import OrganizationNameColumn from 'components/tables/data/signature/columns/OrganizationNameColumn';
import TechnicianNameColumn from 'components/tables/data/signature/columns/TechnicianNameColumn';
import RevisionsColumn from 'components/tables/data/signature/columns/RevisionsColumn';
import DocumentValidUntilColumn from 'components/tables/data/signature/columns/DocumentValidUntilColumn';
import ActionsColumn from 'components/tables/data/signature/columns/ActionsColumn';
import {
  OnStatusChangeParams,
  OnNoLongerAccessible,
  OnStatusChange,
  SignatureRow,
} from 'components/tables/data/signature/types';
import { IconSignature } from '@tabler/icons-react';
import { useBulkSignRevisionModal } from 'components/modals/bulk-sign-revision-modal/BulkSignRevisionModalProvider';
import { map } from 'lodash';
import panic from 'errors/panic';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import useOrganizationModules, {
  getEnabledDocumentKindFilter,
} from 'features/organization-modules/use-organization-modules';

interface SignatureTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

/**
 * Table which displays signatures grouped by KEP message UUID.
 */
const SignatureTableImpl = forwardRef<IDataTablePublic, SignatureTableProps>(
  ({ initialFilters, suppressProfileChangedNotification, suppressSaveProfiles }, ref) => {
    const { getAction, userId } = useApi();
    const { revisions: revisionsModuleEnabled, forms: formsModuleEnabled } = useOrganizationModules();
    const { tableActionMode } = useResponsiveDesign();
    const [api, setApi] = useState<GridApi<SignatureRow> | null>(null);
    const { bulkSignRevisionModal } = useBulkSignRevisionModal();
    const [allSignableRevisions, setAllSignableRevisions] = useState<number[]>([]);
    const [loadingSignableRevisions, setLoadingSignableRevisions] = useState<boolean>(true);
    const objectColumn = useObjectColumn({
      breadcrumbField: 'department.breadcrumbs',
      organizationFilterKey: 'organization.organizationId',
      legacyColumnIds: ['department.departmentName'],
    });

    const [defaultProfileFilters] = useState(() => ({
      'revisionStatus.revisionStatusId': {
        filter: [9], // being-signed by default
      },
    }));

    /**
     * The columns of the table.
     */
    const columns: ColDef[] = useMemo(
      () => [
        {
          field: 'kepMessageUuid',
          headerName: 'KEP UUID',
          sortable: true,
          unSortIcon: true,
          width: 116,
          minWidth: 116,
          maxWidth: 116,
          cellRenderer: KepMessageUuidColumn,
        },
        {
          field: 'revisionStatus.revisionStatusId',
          headerName: 'Stav',
          minWidth: 180,
          sortable: true,
          unSortIcon: true,
          filter: RevisionStatusFilter,
          floatingFilter: true,
          floatingFilterComponent: RevisionStatusFloatingFilter,
          cellRenderer: StatusColumn,
        },
        {
          field: 'revisionStatus.revisionStatusName',
          hide: true,
          filter: true,
          headerName: 'Stav',
        },
        {
          field: 'organization.organizationId',
          hide: true,
          filter: OrganizationMultiColumnFilter,
          headerName: 'ID organizácie',
        },
        {
          field: 'organization.organizationName',
          headerName: 'Organizácia',
          minWidth: 225,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: OrganizationMultiSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: OrganizationNameColumn,
        },
        ...objectColumn.columns,
        {
          field: 'technician.userId',
          hide: true,
          filter: true,
          headerName: 'ID technika',
        },
        {
          field: 'technician.fullName',
          headerName: 'Technik / Vypracovateľ',
          minWidth: 200,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: TechnicianSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: TechnicianNameColumn,
        },
        {
          field: 'revisions',
          headerName: 'Dokumenty',
          minWidth: 250,
          sortable: false,
          cellRenderer: RevisionsColumn,
        },
        {
          field: 'documentValidUntil',
          headerName: 'Deadline na prepečiatkovanie',
          minWidth: 180,
          sortable: true,
          unSortIcon: true,
          filter: DateColumnFilter,
          floatingFilter: true,
          floatingFilterComponent: DateFilter.configure({ filterKey: 'documentValidUntil' }),
          cellRenderer: DocumentValidUntilColumn,
        },
        {
          field: '_actions',
          headerName: '',
          width: getResponsiveActionsColumnWidth(204, tableActionMode),
          minWidth: getResponsiveActionsColumnWidth(204, tableActionMode),
          maxWidth: getResponsiveActionsColumnWidth(204, tableActionMode),
          pinned: 'right',
          cellStyle: { display: 'flex' },
          cellRenderer: ActionsColumn,
        },
      ],
      [objectColumn.columns, tableActionMode]
    );

    const action = useCallback(async () => {
      const listRevisions = getAction('RevisionList');

      const revisionsWithSignature = await listRevisions({
        query: {
          filters: {
            'statusId.in': [9, 10],
            ...getEnabledDocumentKindFilter(revisionsModuleEnabled, formsModuleEnabled),
          },
        },
      });

      // Group revisions by kepMessageUuid
      const grouped = new Map<string, SignatureRow>();

      revisionsWithSignature
        .filter(({ kind }) => (kind === 'revision' ? revisionsModuleEnabled : formsModuleEnabled))
        .filter(({ kepMessageUuid }) => !!kepMessageUuid)
        .forEach((revision) => {
          const uuid = revision.kepMessageUuid!;

          if (!grouped.has(uuid)) {
            grouped.set(uuid, {
              kepMessageUuid: uuid,
              revisionStatus: revision.revisionStatus,
              organization: revision.organization,
              department: revision.department,
              technician: revision.technician,
              revisions: [
                {
                  revisionId: revision.revisionId,
                  revisionName: revision.revisionName,
                  kind: revision.kind === 'revision' ? 'revision' : 'form',
                },
              ],
              documentValidUntil: revision.documentValidUntil,
              kepSignUrl: revision.kepSignUrl,
            });
          } else {
            const existing = grouped.get(uuid)!;

            existing.revisions.push({
              revisionId: revision.revisionId,
              revisionName: revision.revisionName,
              kind: revision.kind === 'revision' ? 'revision' : 'form',
            });

            // Update documentValidUntil to the maximum (furthest into the future)
            if (
              revision.documentValidUntil &&
              (!existing.documentValidUntil ||
                new Date(revision.documentValidUntil) > new Date(existing.documentValidUntil))
            ) {
              existing.documentValidUntil = revision.documentValidUntil;
            }
          }
        });

      return Array.from(grouped.values());
    }, [formsModuleEnabled, getAction, revisionsModuleEnabled, userId]);

    /**
     * Handles the status change of a signature.
     */
    const onStatusChange = useCallback(
      ({ kepMessageUuid, status }: OnStatusChangeParams) => {
        const row = api?.getRowNode(kepMessageUuid);

        if (row && row.data) {
          row.setData({ ...row.data, revisionStatus: status });
        }
      },
      [api]
    );

    /**
     * Removes a row from the table when the user no longer has access to it.
     */
    const onNoLongerAccessible = useCallback(
      (kepMessageUuid: string) => {
        const row = api?.getRowNode(kepMessageUuid);

        if (row && row.data) {
          api?.applyTransaction({ remove: [row.data] });
        }
      },
      [api]
    );

    const contextValue = useMemo(
      () => ({ onStatusChange, onNoLongerAccessible }),
      [onStatusChange, onNoLongerAccessible]
    );

    /**
     * Opens the bulk sign modal with all signable revisions.
     */
    const openBulkSignModal = useCallback(() => {
      bulkSignRevisionModal({ revisionIds: allSignableRevisions });
    }, [bulkSignRevisionModal, allSignableRevisions]);

    useEffect(() => {
      const listRevisions = getAction('RevisionList');

      setLoadingSignableRevisions(true);

      listRevisions({
        query: {
          filters: {
            'technicianId.eq': userId,
            'statusId.in': [8, 9],
            ...getEnabledDocumentKindFilter(revisionsModuleEnabled, formsModuleEnabled),
          },
        },
      })
        .then((signableRevisions) =>
          setAllSignableRevisions(
            map(
              signableRevisions.filter(({ kind }) =>
                kind === 'revision' ? revisionsModuleEnabled : formsModuleEnabled
              ),
              'revisionId'
            )
          )
        )
        .catch(panic)
        .finally(() => setLoadingSignableRevisions(false));
    }, [formsModuleEnabled, getAction, revisionsModuleEnabled, userId]);

    return (
      <SignatureTableContext.Provider value={contextValue}>
        <DataTable
          ref={ref}
          tableId="signatures"
          title="Podpisovanie"
          hideToggleDiscarded
          addButtonIcon={<IconSignature />}
          addButtonText={`Hromadné podpísanie ${!loadingSignableRevisions ? `(${allSignableRevisions.length})` : ''}`}
          onAddButtonClick={openBulkSignModal}
          addButtonLoading={loadingSignableRevisions}
          addButtonDisabled={!loadingSignableRevisions && allSignableRevisions.length === 0}
          getRowId={({ data: { kepMessageUuid } }) => kepMessageUuid}
          columns={columns}
          action={action}
          initialFilters={initialFilters}
          defaultProfileFilters={defaultProfileFilters}
          onReady={setApi}
          suppressSaveProfiles={suppressSaveProfiles}
          suppressProfileChangedNotification={suppressProfileChangedNotification}
          profileColumnAliases={objectColumn.profileColumnAliases}
          profileMigrationReady={objectColumn.profileMigrationReady}
          dataExport={{
            modalTitle: 'Exportovať podpisy',
            fileName: 'podpisy.xlsx',
            columnKeys: [
              'kepMessageUuid',
              'revisionStatus.revisionStatusName',
              'organization.organizationId',
              'organization.organizationName',
              ...objectColumn.columnKeys,
              'technician.userId',
              'technician.fullName',
              'documentValidUntil',
            ],
            columnReplacements: {
              ...objectColumn.columnReplacements,
              'revisionStatus.revisionStatusId': ['revisionStatus.revisionStatusName'],
            },
            ready: objectColumn.exportReady,
            error: objectColumn.exportError,
          }}
        />
      </SignatureTableContext.Provider>
    );
  }
);

interface ISignatureTableContext {
  onStatusChange: OnStatusChange;
  onNoLongerAccessible: OnNoLongerAccessible;
}

const SignatureTableContext = createContext<ISignatureTableContext>(undefined!);

/**
 * Table which displays signatures grouped by KEP message UUID.
 */
const SignatureTable = forwardRef<IDataTablePublic, SignatureTableProps>((props, ref) => {
  return <SignatureTableImpl ref={ref} {...props} />;
});

export default SignatureTable;

/**
 * Hook to access the signature table context.
 */
export function useSignatureTableContext() {
  return useContext(SignatureTableContext);
}
